import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useConfirm } from '../context/ConfirmContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Project, CreateProject, UpdateProject, Customer, CreateProjectPerson, ProjectTag } from '../types';
import { projectsAPI, customersAPI, projectPersonsAPI, api, projectTagsAPI } from '../services/api';
import ProjectsTable from './ProjectsTable';
import Pagination from './Pagination';
import MultiSelectDropdown from './MultiSelectDropdown';
import SearchableDropdown from './SearchableDropdown';
import { addRedirectToUrl, navigateWithRedirect } from '../utils/redirectUtils';
import { saveFiltersToStorage, loadFiltersFromStorage } from '../utils/filterStorage';
import { ProjectStatus, PROJECT_STATUS_LABELS } from '../utils/constants';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [projects, setProjects] = useState<Project[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [projectTags, setProjectTags] = useState<ProjectTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const confirm = useConfirm();

  const [statuses, setStatuses] = useState<{ id: number; title: string }[]>([]);
  const [loadingStatuses, setLoadingStatuses] = useState<boolean>(true);

  const defaultFilters = {
    statusFilter: ProjectStatus.InProgress,
    customerFilter: 0,
    searchText: '',
    tagFilter: [] as number[],
    onlyWithoutTransactions: false,
  };
  const initialFilters = loadFiltersFromStorage('projects', defaultFilters);

  const [statusFilter, setStatusFilter] = useState<number>(initialFilters.statusFilter);
  const [customerFilter, setCustomerFilter] = useState<number>(initialFilters.customerFilter);
  const [searchText, setSearchText] = useState<string>(initialFilters.searchText);
  const [tagFilter, setTagFilter] = useState<number[]>(
    Array.isArray(initialFilters.tagFilter) && typeof initialFilters.tagFilter[0] !== 'string'
      ? initialFilters.tagFilter
      : []
  );
  const [onlyWithoutTransactions, setOnlyWithoutTransactions] = useState<boolean>(initialFilters.onlyWithoutTransactions);

  // Debounced search value sent to API
  const [debouncedSearch, setDebouncedSearch] = useState(searchText);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    setCurrentPage(0);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => setDebouncedSearch(value), SEARCH_DEBOUNCE_MS);
    saveFiltersToStorage('projects', { statusFilter, customerFilter, searchText: value, tagFilter, onlyWithoutTransactions });
  };

  const loadStatuses = async () => {
    setLoadingStatuses(true);
    try {
      const response = await api.get('/statuses');
      const statusesData = response.data.data || response.data;
      setStatuses([{ id: 0, title: 'All status' }, ...statusesData]);
    } catch {
      const fallbackStatuses = Object.entries(PROJECT_STATUS_LABELS).map(([key, label]) => ({
        id: parseInt(key),
        title: label,
      }));
      setStatuses([{ id: 0, title: 'All status' }, ...fallbackStatuses]);
    } finally {
      setLoadingStatuses(false);
    }
  };

  const loadMeta = async () => {
    const [customersData, projectTagsData] = await Promise.all([
      customersAPI.getCustomers(),
      projectTagsAPI.getProjectTags(),
    ]);
    setCustomers(customersData);
    setProjectTags(projectTagsData);
  };

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      const skip = currentPage * pageSize;
      const result = await projectsAPI.getProjects({
        skip,
        take: pageSize,
        customerId: customerFilter > 0 ? customerFilter : undefined,
        statusId: statusFilter !== 0 ? statusFilter : undefined,
        searchText: debouncedSearch || undefined,
        tagIds: tagFilter.length > 0 ? tagFilter : undefined,
        onlyWithoutTransactions: onlyWithoutTransactions || undefined,
      });
      setProjects(result.items);
      setTotalCount(result.totalCount);
      setError(null);
    } catch (err) {
      setError('Failed to load projects');
      console.error('Error loading projects:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, customerFilter, statusFilter, debouncedSearch, tagFilter, onlyWithoutTransactions]);

  useEffect(() => {
    loadStatuses();
    loadMeta();
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const tagOptions = projectTags.map(tag => ({ id: tag.id, name: tag.name.trim() }));

  const handleCreateProject = async (projectData: CreateProject, pendingPersons?: CreateProjectPerson[]) => {
    try {
      setFormLoading(true);
      const createdProject = await projectsAPI.createProject(projectData);
      if (pendingPersons && pendingPersons.length > 0) {
        await Promise.all(
          pendingPersons.map(pp => projectPersonsAPI.createProjectPerson({ ...pp, projectId: createdProject.id }))
        );
      }
      await loadProjects();
      navigateWithRedirect(navigate, '/projects', searchParams);
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || err?.response?.data || err?.message || 'Failed to create project';
      setError(`Failed to create project: ${JSON.stringify(errorMsg)}`);
      alert(`Error creating project. Check console for details.`);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateProject = async (projectData: UpdateProject) => {
    try {
      setFormLoading(true);
      await projectsAPI.updateProject(projectData.id, projectData);
      await loadProjects();
      navigateWithRedirect(navigate, '/projects', searchParams);
    } catch (err) {
      setError('Failed to update project');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteProject = async (id: number) => {
    const isConfirmed = await confirm({
      title: 'Delete project',
      message: 'Are you sure you want to delete this project? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!isConfirmed) return;
    try {
      await projectsAPI.deleteProject(id);
      await loadProjects();
    } catch {
      setError('Failed to delete project');
    }
  };

  const handleEditProject = (project: Project) => {
    navigate(`/projects/edit/${project.id}?redirect=/projects`);
  };

  const handleViewDetails = (id: number) => {
    navigate(`/projects/${id}`);
  };

  const handleCreateTransaction = (projectId: number) => {
    const urlWithRedirect = addRedirectToUrl(
      `/transactions/create?projectId=${projectId}`,
      location.pathname + location.search
    );
    navigate(urlWithRedirect);
  };

  const customerOptions = [
    { value: 0, label: 'All customer' },
    ...customers.map(c => ({ value: c.id, label: c.title })),
  ];

  const handlePageChange = (page: number) => setCurrentPage(page);

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  return (
    <div className="fade-in">
      <div style={{ marginBottom: '2rem' }}>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 style={{
              color: 'var(--text-primary)',
              fontSize: 'var(--font-size-3xl)',
              fontWeight: '700',
              marginBottom: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
            }}>
              Projects
            </h1>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-lg)', margin: 0 }}>
              Manage your project portfolio
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/projects/create?redirect=/projects')}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            Create Project
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card">
        <div className="card-header">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <h5 className="card-title">Project List</h5>
            <div className="ms-auto d-flex flex-wrap gap-2 align-items-end" style={{ justifyContent: 'flex-end' }}>
              <div>
                <input
                  type="text"
                  id="projectSearchText"
                  className="form-control"
                  placeholder="Search projects..."
                  value={searchText}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </div>
              <div>
                <select
                  id="projectStatusFilter"
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value);
                    setStatusFilter(newValue);
                    setCurrentPage(0);
                    saveFiltersToStorage('projects', { statusFilter: newValue, customerFilter, searchText, tagFilter, onlyWithoutTransactions });
                  }}
                  disabled={loadingStatuses}
                >
                  {loadingStatuses ? (
                    <option>Loading...</option>
                  ) : (
                    statuses.map(option => (
                      <option key={option.id} value={option.id}>{option.title}</option>
                    ))
                  )}
                </select>
              </div>
              <div style={{ minWidth: '200px' }}>
                <SearchableDropdown
                  id="projectCustomerFilter"
                  label=""
                  placeholder="All Customers"
                  items={customerOptions}
                  value={customerFilter}
                  onChange={(value) => {
                    const newValue = Number(value);
                    setCustomerFilter(newValue);
                    setCurrentPage(0);
                    saveFiltersToStorage('projects', { statusFilter, customerFilter: newValue, searchText, tagFilter, onlyWithoutTransactions });
                  }}
                  getItemValue={(item) => item.value}
                  getItemLabel={(item) => item.label}
                  searchPlaceholder="Search customers..."
                  noItemsMessage="No customers found"
                  className="filter-dropdown"
                  size="md"
                />
              </div>
              <div style={{ minWidth: '200px' }}>
                <MultiSelectDropdown
                  availableTags={tagOptions}
                  selectedTagIds={tagFilter}
                  onSelectionChange={(newTagIds) => {
                    setTagFilter(newTagIds);
                    setCurrentPage(0);
                    saveFiltersToStorage('projects', { statusFilter, customerFilter, searchText, tagFilter: newTagIds, onlyWithoutTransactions });
                  }}
                  placeholder="No tags selected"
                  showSelectedTags={false}
                  buttonStyle={{ minWidth: '200px' }}
                />
              </div>
              <div className="form-check" style={{ marginBottom: '0.75rem' }}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="projectOnlyWithoutTransactions"
                  checked={onlyWithoutTransactions}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    setOnlyWithoutTransactions(newValue);
                    setCurrentPage(0);
                    saveFiltersToStorage('projects', { statusFilter, customerFilter, searchText, tagFilter, onlyWithoutTransactions: newValue });
                  }}
                />
                <label className="form-check-label" htmlFor="projectOnlyWithoutTransactions">
                  Only without transactions
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '300px' }}>
              <div className="spinner-border" role="status"></div>
              <p className="text-muted">Loading projects...</p>
            </div>
          ) : (
            <>
              <ProjectsTable
                projects={projects}
                statuses={statuses}
                loadingStatuses={loadingStatuses}
                statusFilter={statusFilter}
                customerOptions={customerOptions}
                customerFilter={customerFilter}
                searchText={searchText}
                onlyWithoutTransactions={onlyWithoutTransactions}
                onSearchTextChange={handleSearchChange}
                onStatusFilterChange={(newValue: number) => {
                  setStatusFilter(newValue);
                  setCurrentPage(0);
                  saveFiltersToStorage('projects', { statusFilter: newValue, customerFilter, searchText, tagFilter, onlyWithoutTransactions });
                }}
                onCustomerFilterChange={(newValue: number) => {
                  setCustomerFilter(newValue);
                  setCurrentPage(0);
                  saveFiltersToStorage('projects', { statusFilter, customerFilter: newValue, searchText, tagFilter, onlyWithoutTransactions });
                }}
                onOnlyWithoutTransactionsChange={(newValue: boolean) => {
                  setOnlyWithoutTransactions(newValue);
                  setCurrentPage(0);
                  saveFiltersToStorage('projects', { statusFilter, customerFilter, searchText, tagFilter, onlyWithoutTransactions: newValue });
                }}
                onEdit={handleEditProject}
                onDelete={handleDeleteProject}
                onViewDetails={handleViewDetails}
                onCreateTransaction={handleCreateTransaction}
              />
              {totalCount === 0 ? (
                <div className="text-center text-muted p-5">
                  <div style={{ fontSize: '3rem' }}>
                    <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
                  </div>
                  <h5>No projects found</h5>
                  <p>Try adjusting your search filters or create a new project.</p>
                </div>
              ) : (
                <Pagination
                  currentPage={currentPage}
                  totalItems={totalCount}
                  itemsPerPage={pageSize}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Projects;
